import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, TextField, Checkbox, CircularProgress, IconButton, Tooltip, Link,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel,
  MenuItem, Drawer,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import toast from 'react-hot-toast';
import { useMsal } from '@azure/msal-react';
import PageHeader from '../../ui-components/PageHeader';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { BACKEND_URL } from '../../utils/apiConfig';
import { formatDateISO } from '../../utils/weekUtils';
import {
  initMicrosoftTodoService, getAllTodos, getDefaultList, createTask, completeTask, deleteTask,
  updateTask, createTodoList, deleteTodoList, reopenTask,
  normaliseerChecklist, addChecklistItem, setChecklistItem, deleteChecklistItem, todoDueDate } from '../../utils/microsoftTodoService';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { bevestig } from '../../utils/bevestig';

// Testfase: import eerst alleen voor Lars (en Joris om mee te kijken) — 1 sep.
const IMPORT_TESTERS = new Set(['lars@fullframe.nl', 'joris@fullframe.nl']);
const GRAPH = 'https://graph.microsoft.com/v1.0';

async function graphAlles(url, token) {
  const uit = [];
  let volgende = url;
  while (volgende) {
    const r = await fetch(volgende, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) throw new Error(`Graph ${r.status}`);
    const d = await r.json();
    uit.push(...(d.value || []));
    volgende = d['@odata.nextLink'] || null;
  }
  return uit;
}

/** Importdialog: kies per Microsoft To Do-lijst (groep) wat mee moet. */
function ImportDialog({ open, onClose, onKlaar, bestaande }) {
  const { instance, accounts } = useMsal();
  const [lijsten, setLijsten] = useState(null);
  const [gekozen, setGekozen] = useState(new Set());
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);

  const graphToken = useCallback(async () => {
    const req = { scopes: ['Tasks.Read'], account: accounts[0] };
    try { return (await instance.acquireTokenSilent(req)).accessToken; }
    catch { return (await instance.acquireTokenPopup(req)).accessToken; }
  }, [instance, accounts]);

  useEffect(() => {
    if (!open) return;
    setLijsten(null); setFout(null); setGekozen(new Set());
    (async () => {
      try {
        const token = await graphToken();
        const ls = await graphAlles(`${GRAPH}/me/todo/lists?$top=50`, token);
        // Per lijst de open taken alvast ophalen, dan klopt de teller meteen.
        const metTaken = [];
        for (const l of ls) {
          // $expand=checklistItems: bij Lars zijn taken vaak projecten met
          // checklist-items als echte to-do's (1 sep) — die splitsen we uit.
          const taken = (await graphAlles(
            `${GRAPH}/me/todo/lists/${l.id}/tasks?$top=200&$expand=checklistItems`, token,
          )).filter((t) => t.status !== 'completed');
          metTaken.push({ id: l.id, naam: l.displayName, taken });
        }
        setLijsten(metTaken);
      } catch (e) {
        setFout(String(e.message || e));
      }
    })();
  }, [open, graphToken]);

  const importeer = async () => {
    setBezig(true);
    let ok = 0; let dubbel = 0; let mislukt = 0;
    try {
      const bewaar = async (regel) => {
        if (bestaande.some((b) => b.title === regel.title && (b.groep || null) === (regel.groep || null))) {
          dubbel += 1; return;
        }
        try {
          const r = await authFetch(`${BACKEND_URL}/Api/weekplan-tasks`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...regel, source: 'ms-todo' }),
          });
          if (!r.ok) throw new Error();
          ok += 1;
        } catch { mislukt += 1; }
      };
      for (const l of (lijsten || []).filter((x) => gekozen.has(x.id))) {
        for (const t of l.taken) {
          const titel = (t.title || '').trim();
          if (!titel) continue;
          const deadline = todoDueDate(t) || undefined;
          const openItems = (t.checklistItems || []).filter((c) => !c.isChecked && (c.displayName || '').trim());
          if (openItems.length) {
            // Taak = project (bv. "9768. ICTRecht - Webinar SEPT"); de
            // checklist-items zijn de echte to-do's en houden die groep.
            for (const c of openItems) {
              await bewaar({ title: c.displayName.trim(), deadline, groep: titel.slice(0, 200) });
            }
          } else {
            await bewaar({
              title: titel,
              notes: (t.body?.content || '').trim().slice(0, 2000) || undefined,
              deadline,
              groep: l.naam,
            });
          }
        }
      }
      toast.success(`${ok} to-do's geïmporteerd${dubbel ? ` (${dubbel} stonden er al)` : ''}${mislukt ? ` — ${mislukt} mislukt` : ''}`);
      onKlaar();
    } finally {
      setBezig(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !bezig && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Importeren uit Microsoft To Do</DialogTitle>
      <DialogContent dividers>
        {fout ? (
          <Typography sx={{ fontFamily: FONT, color: '#b91c1c', fontSize: 13.5 }}>
            Kon je Microsoft To Do niet lezen ({fout}). Geef toestemming in de popup en probeer opnieuw.
          </Typography>
        ) : lijsten === null ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 2 }}>
            <CircularProgress size={16} />
            <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>Lijsten ophalen…</Typography>
          </Box>
        ) : (
          <Box>
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', mb: 1 }}>
              Kies welke lijsten (groepen) je meeneemt — de groepsnaam blijft behouden.
              Afgeronde to-do's slaan we over.
            </Typography>
            {lijsten.map((l) => (
              <FormControlLabel key={l.id} sx={{ display: 'flex', m: 0 }}
                control={(
                  <Checkbox size="small" checked={gekozen.has(l.id)}
                    onChange={(e) => setGekozen((prev) => {
                      const n = new Set(prev);
                      if (e.target.checked) n.add(l.id); else n.delete(l.id);
                      return n;
                    })} />
                )}
                label={(
                  <Typography sx={{ fontFamily: FONT, fontSize: 13.5 }}>
                    {l.naam} <Box component="span" sx={{ color: '#94a3b8' }}>({l.taken.length} open)</Box>
                  </Typography>
                )} />
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={bezig} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" onClick={importeer} disabled={bezig || !gekozen.size}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          {bezig ? 'Bezig…' : `Importeer ${gekozen.size} lijst${gekozen.size === 1 ? '' : 'en'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const FONT = '"Inter", system-ui, sans-serif';
const NAV_FONT = '"Manrope", system-ui, sans-serif';
// Zelfde statussen als de Notes-boom: dit is 'lopend werk'.
const OPEN_STATUSSEN = new Set(['In progress', 'Offerte', 'Lead', 'On Hold']);
const OFFERTE_STATUSSEN = new Set(['Offerte', 'Lead']);
// /Api/projects levert Airtable-vorm: namen staan in lookup-velden (Codex 2 sep).
const ROLVELDEN = ['Projectleider (Volledige naam)', 'Naam (from Creative)', 'ROLLUP - Editors', 'Naam (from Animator)', 'Naam (from Cameraman)', 'ROLLUP - Productie',
  'projectleider', 'creative', 'editor', 'animator', 'cameraman', 'productie'];

const vandaagISO = () => formatDateISO(new Date());
const plusDagen = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const datumKort = (iso) => {
  if (!iso) return '';
  const dag = String(iso).slice(0, 10);
  if (dag === vandaagISO()) return 'vandaag';
  if (dag === plusDagen(1)) return 'morgen';
  return new Date(dag).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
};

/** Lijsten die je zelf aanmaakte maar nog leeg zijn. Een lijst bestaat verder
 *  alleen als er een to-do in zit (`groep` staat op de to-do zelf), dus zonder
 *  dit verdween een verse lijst meteen weer uit de kolom. */
const lijstSleutel = (email) => `mijn-todos-lijsten:${email || 'anon'}`;
const leesEigenLijsten = (email) => {
  try {
    const lijst = JSON.parse(localStorage.getItem(lijstSleutel(email)) || '[]');
    return Array.isArray(lijst) ? lijst.filter((n) => typeof n === 'string' && n.trim()) : [];
  } catch { return []; }
};

// Zo heet een projectlijst — gelijk aan de Notes-boom ("9760. Opnames …"), zodat
// een to-do die je via de projectkiezer aanmaakte in dezelfde lijst valt.
const projectLijstNaam = (p) => {
  const f = p.fields || p;
  const nummer = f.projectNumber || f.Projectnummer || '';
  const naam = f.projectName || f.Projectnaam || f.projectNameFull || 'Project';
  return [nummer, naam].filter(Boolean).join('. ');
};
const normaliseer = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
// "9425. DELA HUB video's" / "9425 - …" / "9425 …" → "9425". De lijstnaam is in Microsoft To Do het
// enige dat we kunnen opslaan (Graph kent geen groepen of metadata op lijsten), dus het nummer
// vooraan is de koppeling naar het project — en via het project naar het account.
const nummerUitLijstnaam = (naam) => (String(naam || '').match(/^(\d{3,5})(?:[.\s:–-]|$)/) || [])[1] || null;
const openAccountsSleutel = (email) => `mijn-todos-open-accounts:${email || 'anon'}`;

/** Nieuwe lijst: vrije naam of een van je projecten (server-side zoeken). */
function NieuweLijstDialog({ open, onClose, onKies, bestaande }) {
  const [naam, setNaam] = useState('');
  const [zoek, setZoek] = useState('');
  const [treffers, setTreffers] = useState(null);
  const [bezig, setBezig] = useState(false);
  useEffect(() => { if (!open) { setNaam(''); setZoek(''); setTreffers(null); } }, [open]);
  useEffect(() => {
    const q = zoek.trim();
    if (q.length < 2) { setTreffers(null); return undefined; }
    let geldig = true; setBezig(true);
    const timer = setTimeout(async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/projects?search=${encodeURIComponent(q)}`);
        const d = await leesJson(r, 'Projecten zoeken');
        if (geldig) setTreffers((d.projects || []).slice(0, 12));
      } catch (e) { if (geldig) { meldFout(e, 'Projecten zoeken'); setTreffers([]); } } finally { if (geldig) setBezig(false); }
    }, 350);
    return () => { geldig = false; clearTimeout(timer); };
  }, [zoek]);
  const bevestig = (waarde) => {
    const schoon = (waarde || '').trim().slice(0, 200);
    if (!schoon) return;
    if (bestaande.some((n) => n.toLowerCase() === schoon.toLowerCase())) { toast.error('Die lijst bestaat al'); return; }
    onKies(schoon);
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Nieuwe lijst</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth size="small" label="Naam van de lijst" value={naam}
          onChange={(e) => setNaam(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); bevestig(naam); } }} sx={{ mt: 1, mb: 2.5 }} />
        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, mb: 1 }}>
          of pak een ander project
        </Typography>
        <TextField fullWidth size="small" placeholder="Zoek op projectnummer of naam…" value={zoek} onChange={(e) => setZoek(e.target.value)} />
        <Box sx={{ mt: 1, maxHeight: 220, overflowY: 'auto' }}>
          {bezig && <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={18} /></Box>}
          {!bezig && treffers?.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#94a3b8', py: 1 }}>Geen project gevonden.</Typography>}
          {!bezig && (treffers || []).map((p) => (
            <Box key={p.id || projectLijstNaam(p)} onClick={() => bevestig(projectLijstNaam(p))}
              sx={{ px: 1, py: 0.75, borderRadius: 1, cursor: 'pointer', fontFamily: FONT, fontSize: 13.5, '&:hover': { bgcolor: '#eef2ff' } }}>
              {projectLijstNaam(p)}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" onClick={() => bevestig(naam)} disabled={!naam.trim()} sx={{ textTransform: 'none', fontWeight: 700 }}>Lijst maken</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Zijpaneel rechts: titel, lijst, deadline en losse notities bij één to-do. */
function TodoPaneel({ item, lijsten, onClose, onWijzig, onVerwijder, onVinkAf, onSubtaak }) {
  const [titel, setTitel] = useState(item?.title || '');
  const [nieuweStap, setNieuweStap] = useState('');
  const [notities, setNotities] = useState(item?.notes || '');
  useEffect(() => { setTitel(item?.title || ''); setNotities(item?.notes || ''); }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!item) return null;
  const bewaarTitel = () => { const t = titel.trim(); if (t && t !== item.title) onWijzig(item, { title: t }); };
  const bewaarNotities = () => { if ((notities || '') !== (item.notes || '')) onWijzig(item, { notes: notities || null }); };
  const knop = (label, actief, onClick) => (
    <Button key={label} size="small" variant={actief ? 'contained' : 'outlined'} onClick={onClick}
      sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, py: 0.25, px: 1.25, minWidth: 0 }}>{label}</Button>
  );
  const deadline = item.deadline ? String(item.deadline).slice(0, 10) : '';
  return (
    <Drawer anchor="right" open={!!item} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, p: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Checkbox size="small" checked={!!item.net_af} onChange={() => onVinkAf(item)} sx={{ p: 0.5 }} />
        <Typography sx={{ fontFamily: NAV_FONT, fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#94a3b8', flex: 1 }}>To-do</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      <TextField fullWidth multiline value={titel} onChange={(e) => setTitel(e.target.value)} onBlur={bewaarTitel}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
        inputProps={{ style: { fontFamily: FONT, fontSize: 16, fontWeight: 600, lineHeight: 1.4 } }} sx={{ mb: 2.5 }} />

      {/* Subtaken = checklist-items in Microsoft To Do ("Volgende stap") — zelfde data als de To Do-app */}
      <Typography sx={{ fontFamily: NAV_FONT, fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.5 }}>
        Subtaken{item.checklist?.length ? ` · ${item.checklist.filter((c) => c.done).length} van ${item.checklist.length}` : ''}
      </Typography>
      <Box sx={{ mb: 2.5 }}>
        {(item.checklist || []).map((c) => (
          <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, '&:hover .sub-del': { opacity: 1 } }}>
            <Checkbox size="small" checked={c.done} onChange={(e) => onSubtaak(item, 'toggle', c, e.target.checked)} sx={{ p: 0.5 }} />
            <Typography sx={{ fontFamily: FONT, fontSize: 14, flex: 1, textDecoration: c.done ? 'line-through' : 'none', color: c.done ? '#94a3b8' : '#0f172a' }}>{c.title}</Typography>
            <IconButton className="sub-del" size="small" onClick={() => onSubtaak(item, 'delete', c)} sx={{ opacity: 0, transition: 'opacity 0.15s' }}>
              <CloseIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
            </IconButton>
          </Box>
        ))}
        <TextField size="small" fullWidth placeholder="+ Volgende stap" value={nieuweStap} onChange={(e) => setNieuweStap(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && nieuweStap.trim()) { onSubtaak(item, 'add', null, nieuweStap.trim()); setNieuweStap(''); } }}
          inputProps={{ style: { fontFamily: FONT, fontSize: 14 } }} sx={{ mt: 0.5 }} />
      </Box>

      <Typography sx={{ fontFamily: NAV_FONT, fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.75 }}>Deadline</Typography>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
        {knop('Vandaag', deadline === vandaagISO(), () => onWijzig(item, { deadline: vandaagISO() }))}
        {knop('Morgen', deadline === plusDagen(1), () => onWijzig(item, { deadline: plusDagen(1) }))}
        {knop('Volgende week', deadline === plusDagen(7), () => onWijzig(item, { deadline: plusDagen(7) }))}
        {deadline && knop('Geen', false, () => onWijzig(item, { deadline: null }))}
      </Box>
      <TextField type="date" size="small" value={deadline} onChange={(e) => onWijzig(item, { deadline: e.target.value || null })} sx={{ mb: 2.5, width: 180 }} />

      <Typography sx={{ fontFamily: NAV_FONT, fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.75 }}>Lijst</Typography>
      <TextField select size="small" fullWidth value={item.groep || 'Algemeen'} sx={{ mb: 2.5 }}
        onChange={(e) => onWijzig(item, { groep: e.target.value === 'Algemeen' ? null : e.target.value })}>
        {lijsten.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
      </TextField>

      <Typography sx={{ fontFamily: NAV_FONT, fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.75 }}>Notities</Typography>
      <TextField fullWidth multiline minRows={5} placeholder="Korte aantekeningen bij deze to-do…" value={notities}
        onChange={(e) => setNotities(e.target.value)} onBlur={bewaarNotities}
        inputProps={{ style: { fontFamily: FONT, fontSize: 14, lineHeight: 1.6 } }} sx={{ mb: 3 }} />

      <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8' }}>Wijzigingen worden direct bewaard.</Typography>
        <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => { onVerwijder(item); onClose(); }} sx={{ textTransform: 'none', fontWeight: 700 }}>
          Verwijderen
        </Button>
      </Box>
    </Drawer>
  );
}

/**
 * Mijn To-Do's — Microsoft To Do-stijl: razendsnel taken vangen (Joris, 1 sep).
 * Privétaken via /Api/weekplan-tasks: alleen jij ziet ze, ze staan nergens
 * anders. Zware projecttaken horen op /mijn-taken; dit is het kladblok.
 *
 * Links een tool-eigen kolom (policies/ff-design.md): Mijn dag, Gepland, dan je
 * eigen lopende projecten en offertes (uit /Api/projects, op naam gematcht) en
 * je eigen lijsten. Rechts de to-do's; klikken opent een zijpaneel met notities.
 */
export default function MijnTodos() {
  const { instance, accounts } = useMsal();
  const mijnEmail = (accounts[0]?.username || '').toLowerCase();
  const mijnNaam = normaliseer(accounts[0]?.name);
  const [items, setItems] = useState(null);
  const [msLijsten, setMsLijsten] = useState([]);   // Microsoft To Do-lijsten (incl. de standaardlijst)
  const [legacy, setLegacy] = useState([]);         // oude privé-to-do's in onze database, nog niet overgezet
  const [legacyBezig, setLegacyBezig] = useState(false);
  const [projecten, setProjecten] = useState([]); // mijn lopende projecten/offertes
  const [tekst, setTekst] = useState('');
  const [bezig, setBezig] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [lijstOpen, setLijstOpen] = useState(false);
  const [weergave, setWeergave] = useState('dag'); // 'dag' | 'gepland' | 'alles' | { lijst }
  const [eigenLijsten, setEigenLijsten] = useState([]);
  const [openId, setOpenId] = useState(null); // to-do in het zijpaneel
  // Accounts → projecten, dezelfde bron als Mijn Notes (Joris, 7 sep: "zelfde structuur als mijn notes").
  const [boom, setBoom] = useState([]);
  const [openAccounts, setOpenAccounts] = useState(new Set());
  const [boomZoek, setBoomZoek] = useState('');
  const invoerRef = useRef(null);

  useEffect(() => { setEigenLijsten(leesEigenLijsten(mijnEmail)); }, [mijnEmail]);
  const bewaarEigenLijsten = (lijst) => {
    setEigenLijsten(lijst);
    try { localStorage.setItem(lijstSleutel(mijnEmail), JSON.stringify(lijst)); } catch { /* privémodus */ }
  };

  // Bron = Microsoft To Do van de ingelogde gebruiker (besluit Joris, 6 sep): geen kopie meer in onze
  // database. De standaardlijst ("Taken") heet hier 'Algemeen'; andere lijsten zijn eigen/projectlijsten.
  const laad = useCallback(async () => {
    try {
      if (accounts[0]) initMicrosoftTodoService(instance, accounts[0]);
      const { lists, tasks } = await getAllTodos();
      setMsLijsten(lists);
      setItems(tasks.map(({ task: t, list, isDefault }) => ({
        id: t.id, listId: list.id, groep: isDefault ? null : list.displayName,
        title: t.title || 'Naamloos', notes: t.body?.content || '',
        deadline: todoDueDate(t),
        importance: t.importance || 'normal', createdAt: t.createdDateTime || '', net_af: false,
        checklist: normaliseerChecklist(t),
      })).sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))));
    } catch (e) { setItems([]); toast.error(`To-do’s laden mislukt: ${e.message}`); }
    // Oude privé-to-do's (onze database) — alleen tonen om over te zetten.
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/weekplan-tasks`);
      const d = r.ok ? await r.json() : { tasks: [] };
      setLegacy((d.tasks || []).filter((t) => !t.done));
    } catch { /* geen archief */ }
  }, [instance, accounts]);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => registreerOngedaanSneltoets(), []);
  useEffect(() => {
    let geldig = true;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/project-notes/boom`);
        const d = await leesJson(r, 'Accounts laden');
        if (geldig) setBoom(d.accounts || []);
      } catch (e) { if (geldig) meldFout(e, 'Accounts laden'); }
    })();
    return () => { geldig = false; };
  }, []);
  useEffect(() => {
    try { setOpenAccounts(new Set(JSON.parse(localStorage.getItem(openAccountsSleutel(mijnEmail)) || '[]'))); } catch { /* leeg */ }
  }, [mijnEmail]);
  const toggleAccount = (account) => setOpenAccounts((prev) => {
    const n = new Set(prev); if (n.has(account)) n.delete(account); else n.add(account);
    try { localStorage.setItem(openAccountsSleutel(mijnEmail), JSON.stringify([...n])); } catch { /* privémodus */ }
    return n;
  });

  const lijstIdVoor = useCallback(async (naam) => {
    if (!naam || naam === 'Algemeen') return (msLijsten.find((l) => l.isDefault) || await getDefaultList()).id;
    const bestaand = msLijsten.find((l) => l.displayName === naam);
    if (bestaand) return bestaand.id;
    const nieuw = await createTodoList(naam);
    setMsLijsten((prev) => [...prev, { ...nieuw, isDefault: false }]);
    return nieuw.id;
  }, [msLijsten]);

  const maakLijst = async (naam) => {
    try { await lijstIdVoor(naam); } catch { toast.error('Lijst aanmaken mislukt'); }
  };

  // Archief van de oude privé-to-do's: wat uit de eerdere To Do-import kwam staat dáár al
  // (alleen opruimen); wat hier zelf is aangemaakt kan naar To Do (zonder dubbelingen).
  const legacyImport = useMemo(() => legacy.filter((t) => t.source === 'ms-todo'), [legacy]);
  const legacyEigen = useMemo(() => legacy.filter((t) => t.source !== 'ms-todo'), [legacy]);
  const sluitArchief = async (lijst) => {
    let gelukt = 0;
    for (const t of lijst) {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/weekplan-tasks/${t.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ done: true }) });
        if (r.ok) gelukt += 1;
      } catch { /* volgende */ }
    }
    return gelukt;
  };
  const ruimImportOp = async () => {
    if (!legacyImport.length || legacyBezig) return;
    if (!(await bevestig({
      titel: 'Oude import archiveren?',
      tekst: `${legacyImport.length} to-do's uit de eerdere import worden hier gearchiveerd. Ze blijven gewoon in Microsoft To Do staan.`,
      knop: 'Archiveren',
    }))) return;
    setLegacyBezig(true);
    try { const n = await sluitArchief(legacyImport); toast.success(`${n} geïmporteerde to-do's gearchiveerd — ze staan in Microsoft To Do`); await laad(); }
    finally { setLegacyBezig(false); }
  };
  const zetLegacyOver = async () => {
    if (!legacyEigen.length || legacyBezig) return;
    setLegacyBezig(true);
    let gelukt = 0, overgeslagen = 0;
    try {
      const doel = await lijstIdVoor(null);
      const bestaand = new Set((items || []).map((t) => String(t.title || '').trim().toLowerCase()));
      for (const t of legacyEigen) {
        try {
          if (bestaand.has(String(t.title || '').trim().toLowerCase())) { overgeslagen += 1; }
          else await createTask(doel, { title: t.title, body: t.notes || undefined, dueDate: t.deadline || undefined });
          await authFetch(`${BACKEND_URL}/Api/weekplan-tasks/${t.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ done: true }) });
          gelukt += 1;
        } catch { /* volgende */ }
      }
      toast.success(`${gelukt - overgeslagen} to-do's naar Microsoft To Do gezet${overgeslagen ? `, ${overgeslagen} stonden er al` : ''}`);
      await laad();
    } finally { setLegacyBezig(false); }
  };

  // Mijn projecten: lopend werk waar mijn naam op staat (PL, creative, editor,
  // animator, cameraman of productie). Zonder naam-match blijft de lijst leeg
  // en kun je nog altijd een project kiezen via "Nieuwe lijst".
  useEffect(() => {
    if (!mijnNaam) return;
    let geldig = true;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/projects`);
        const d = await leesJson(r, 'Projectlijst laden');
        const mijne = (d.projects || []).filter((p) => {
          const f = p.fields || p;
          if (!OPEN_STATUSSEN.has(f.status || f.Status)) return false;
          return ROLVELDEN.some((veld) => {
            const w = f[veld];
            const namen = Array.isArray(w) ? w : (w ? [w] : []);
            // record-id's (rec…) zijn geen namen; alleen echte namen vergelijken
            return namen.some((n) => typeof n === 'string' && !/^rec[a-zA-Z0-9]{14}$/.test(n) && normaliseer(n) === mijnNaam);
          });
        }).map((p) => {
          const f = p.fields || p;
          return { id: p.id, naam: projectLijstNaam(p), offerte: OFFERTE_STATUSSEN.has(f.status || f.Status), nummer: String(f.projectNumber || f.Projectnummer || '') };
        }).sort((a, b) => b.nummer.localeCompare(a.nummer));
        if (geldig) setProjecten(mijne);
      } catch (e) { if (geldig) meldFout(e, 'Projectlijst laden'); /* projectlijst is een extra; to-do's werken zonder */ }
    })();
    return () => { geldig = false; };
  }, [mijnNaam]);

  // ?voegtoe=<tekst>&meeting=<id>&titel=<meetingtitel> — link uit de verslag-mail na een externe
  // meeting (17 sep): het actiepunt komt meteen in Microsoft To Do, met ongedaan-maken. Eén keer
  // per paginalading; daarna gaan de parameters uit de URL zodat verversen niet dubbel toevoegt.
  const [zoekParams, setZoekParams] = useSearchParams();
  const voegtoeGedaan = useRef(false);
  useEffect(() => {
    const tekstUitLink = (zoekParams.get('voegtoe') || '').trim();
    if (!tekstUitLink || items === null || voegtoeGedaan.current) return;
    voegtoeGedaan.current = true;
    const meetingId = zoekParams.get('meeting');
    const meetingTitel = zoekParams.get('titel') || '';
    (async () => {
      try {
        const bestaat = (items || []).some((i) => (i.title || '').trim().toLowerCase() === tekstUitLink.toLowerCase() && !i.net_af);
        if (bestaat) { toast(`"${tekstUitLink}" staat al in je lijst`); return; }
        const listId = await lijstIdVoor(null);
        const body = meetingId ? `Uit meeting${meetingTitel ? ` "${meetingTitel}"` : ''}: ${window.location.origin}/MeetingDashboard/${meetingId}` : '';
        const t = await createTask(listId, { title: tekstUitLink, body: body || undefined });
        setItems((prev) => [{ id: t.id, listId, groep: null, title: t.title || tekstUitLink, notes: body, deadline: null, importance: 'normal', createdAt: t.createdDateTime || '', net_af: false }, ...(prev || [])]);
        biedOngedaanAan(`"${tekstUitLink}" toegevoegd aan je to-do's`, async () => {
          await deleteTask(listId, t.id);
          setItems((prev) => (prev || []).filter((i) => i.id !== t.id));
        });
      } catch (e) { toast.error(`Toevoegen uit de mail mislukt: ${e.message}`); }
      finally { setZoekParams({}, { replace: true }); }
    })();
  }, [zoekParams, items, lijstIdVoor, setZoekParams]);

  const actieveLijst = typeof weergave === 'object' ? weergave.lijst : null;

  const voegToe = async () => {
    const titel = tekst.trim();
    if (!titel || bezig) return;
    setBezig(true); setTekst('');
    const extra = {};
    if (actieveLijst && actieveLijst !== 'Algemeen') extra.groep = actieveLijst;
    else if (actiefAccount) extra.groep = indeling.accounts.find((a) => a.account === actiefAccount)?.algemeenLijst || actiefAccount;
    if (weergave === 'dag') extra.deadline = vandaagISO();
    try {
      const listId = await lijstIdVoor(extra.groep || null);
      const t = await createTask(listId, { title: titel, dueDate: extra.deadline || undefined });
      setItems((prev) => [{
        id: t.id, listId, groep: extra.groep || null, title: t.title || titel, notes: '',
        deadline: extra.deadline || null, importance: 'normal', createdAt: t.createdDateTime || '', net_af: false,
      }, ...(prev || [])]);
    } catch { setTekst(titel); toast.error('Toevoegen mislukt'); } finally { setBezig(false); invoerRef.current?.focus(); }
  };

  const wijzig = async (item, velden) => {
    const vorige = { ...item };
    setItems((prev) => prev.map((t) => (t.id === item.id ? { ...t, ...velden } : t)));
    try {
      if ('groep' in velden && (velden.groep || null) !== (item.groep || null)) {
        // Graph kent geen 'verplaatsen': aanmaken in de nieuwe lijst en het origineel weghalen.
        const doel = await lijstIdVoor(velden.groep || null);
        const nieuw = await createTask(doel, { title: item.title, body: item.notes || undefined, dueDate: item.deadline || undefined, importance: item.importance });
        for (const c of item.checklist || []) {
          try { const n = await addChecklistItem(doel, nieuw.id, c.title); if (c.done && n?.id) await setChecklistItem(doel, nieuw.id, n.id, true); }
          catch { /* subtaak kwijt is erger dan een dubbele melding; hoofdtaak staat er */ }
        }
        await deleteTask(item.listId, item.id);
        setItems((prev) => prev.map((t) => (t.id === item.id ? { ...t, id: nieuw.id, listId: doel, groep: velden.groep || null } : t)));
        if (openId === item.id) setOpenId(nieuw.id);
        return;
      }
      const patch = {};
      if ('title' in velden) patch.title = velden.title;
      if ('notes' in velden) patch.body = velden.notes || '';
      if ('deadline' in velden) patch.dueDate = velden.deadline || null;
      if (Object.keys(patch).length) await updateTask(item.listId, item.id, patch);
    } catch {
      toast.error('Opslaan mislukt');
      setItems((prev) => prev.map((t) => (t.id === item.id ? vorige : t)));
    }
  };

  /** Subtaken (checklist-items in Microsoft To Do): afvinken, toevoegen, weghalen. */
  const subtaak = async (item, actie, sub, waarde) => {
    const zet = (fn) => setItems((prev) => prev.map((t) => (t.id === item.id ? { ...t, checklist: fn(t.checklist || []) } : t)));
    try {
      if (actie === 'toggle') { zet((l) => l.map((c) => (c.id === sub.id ? { ...c, done: waarde } : c))); await setChecklistItem(item.listId, item.id, sub.id, waarde); }
      else if (actie === 'add') { const n = await addChecklistItem(item.listId, item.id, waarde); zet((l) => [...l, { id: n.id, title: n.displayName || waarde, done: false }]); }
      else if (actie === 'delete') {
        zet((l) => l.filter((c) => c.id !== sub.id));
        await deleteChecklistItem(item.listId, item.id, sub.id);
        // Geen bevestiging vooraf; wel terug te halen — de subtaak wordt opnieuw aangemaakt.
        biedOngedaanAan(`Subtaak "${sub.title}" verwijderd`, async () => {
          await addChecklistItem(item.listId, item.id, sub.title);
          await laad();
        });
      }
    } catch (e) { toast.error(`Subtaak bijwerken mislukt: ${e.message}`); laad(); }
  };

  const vinkAf = async (item) => {
    setItems((prev) => prev.map((t) => (t.id === item.id ? { ...t, net_af: !t.net_af } : t)));
    try {
      if (!item.net_af) {
        await completeTask(item.listId, item.id);
        setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== item.id || !t.net_af)), 1200);
        biedOngedaanAan(`"${item.title}" afgevinkt`, async () => {
          await reopenTask(item.listId, item.id);
          setItems((prev) => (prev || []).some((t) => t.id === item.id)
            ? prev.map((t) => (t.id === item.id ? { ...t, net_af: false } : t))
            : [{ ...item, net_af: false }, ...(prev || [])]);
        });
      } else {
        await updateTask(item.listId, item.id, {}); // heropenen kan in To Do zelf; hier alleen visueel terug
      }
    } catch {
      toast.error('Afvinken mislukt');
      setItems((prev) => prev.map((t) => (t.id === item.id ? { ...t, net_af: false } : t)));
    }
  };

  const verwijder = async (item) => {
    setItems((prev) => prev.filter((t) => t.id !== item.id));
    if (openId === item.id) setOpenId(null);
    try {
      await deleteTask(item.listId, item.id);
      // Verwijderen zonder vraag vooraf, maar met terugweg: de to-do komt terug in dezelfde lijst.
      biedOngedaanAan(`"${item.title}" verwijderd`, async () => {
        await createTask(item.listId, {
          title: item.title, body: item.notes || undefined,
          dueDate: item.deadline || undefined, importance: item.importance || 'normal',
        });
        await laad();
      });
    } catch { toast.error('Verwijderen mislukt'); laad(); }
  };

  const verwijderLijst = async (naam) => {
    const inLijst = (items || []).filter((t) => (t.groep || 'Algemeen') === naam);
    if (inLijst.length && !(await bevestig({
      titel: `Lijst "${naam}" verwijderen?`,
      tekst: `De hele lijst met ${inLijst.length} to-do's wordt verwijderd, ook in Microsoft To Do. Dit kan niet ongedaan worden gemaakt.`,
      knop: 'Verwijderen',
      gevaarlijk: true,
    }))) return;
    setItems((prev) => prev.filter((t) => (t.groep || 'Algemeen') !== naam));
    if (actieveLijst === naam) setWeergave('dag');
    const lijst = msLijsten.find((l) => l.displayName === naam);
    if (!lijst) return;
    try { await deleteTodoList(lijst.id); setMsLijsten((prev) => prev.filter((l) => l.id !== lijst.id)); }
    catch { toast.error('Lijst verwijderen mislukt'); laad(); }
  };

  const aantalIn = useMemo(() => {
    const per = new Map();
    for (const t of items || []) { const n = t.groep || 'Algemeen'; per.set(n, (per.get(n) || 0) + 1); }
    return per;
  }, [items]);
  // ── Indeling zoals Mijn Notes: Accounts → projecten ────────────────────────
  // In Microsoft To Do is een lijst het enige wat bestaat (geen groepen, geen metadata).
  // Afspraak: één lijst per project, naam "<nummer>. <naam>"; het account komt uit onze
  // projectspiegel (zelfde boom als Notes). Een lijst met precies de accountnaam is het
  // "Algemeen" van dat account (to-do's zonder project). Al het andere is een eigen lijst.
  const indeling = useMemo(() => {
    const perNummer = new Map(); const perNaam = new Map(); const accountPerNaam = new Map();
    for (const acc of boom) {
      accountPerNaam.set(normaliseer(acc.account), acc.account);
      for (const p of acc.projecten || []) {
        const info = { account: acc.account, project: p, lijstNaam: `${p.nummer}. ${p.naam}` };
        if (p.nummer) perNummer.set(String(p.nummer), info);
        perNaam.set(normaliseer(p.naam), info); perNaam.set(normaliseer(info.lijstNaam), info);
      }
    }
    const alleNamen = new Set([...msLijsten.filter((l) => !l.isDefault).map((l) => l.displayName), ...aantalIn.keys(), ...eigenLijsten]);
    alleNamen.delete('Algemeen');
    const perAccount = new Map(); // account → { algemeenLijst, projecten: Map(projectId → {project, lijstNaam, aantal}) }
    const eigen = [];
    const zorgAccount = (account) => {
      if (!perAccount.has(account)) perAccount.set(account, { account, algemeenLijst: null, projecten: new Map() });
      return perAccount.get(account);
    };
    for (const naam of alleNamen) {
      const nr = nummerUitLijstnaam(naam);
      const info = (nr && perNummer.get(nr)) || perNaam.get(normaliseer(naam));
      if (info) {
        const acc = zorgAccount(info.account);
        const bestaand = acc.projecten.get(info.project.id);
        acc.projecten.set(info.project.id, { project: info.project, lijstNaam: naam, aantal: (bestaand?.aantal || 0) + (aantalIn.get(naam) || 0) });
        continue;
      }
      const account = accountPerNaam.get(normaliseer(naam));
      if (account) { zorgAccount(account).algemeenLijst = naam; continue; }
      eigen.push(naam);
    }
    // Mijn lopende projecten altijd zichtbaar onder hun account (ook zonder lijst) — gedimd.
    for (const p of projecten) {
      const info = perNummer.get(p.nummer) || perNaam.get(normaliseer(p.naam));
      if (!info) continue;
      const acc = zorgAccount(info.account);
      if (!acc.projecten.has(info.project.id)) acc.projecten.set(info.project.id, { project: info.project, lijstNaam: info.lijstNaam, aantal: 0, mijn: true });
      else acc.projecten.get(info.project.id).mijn = true;
    }
    const accounts = [...perAccount.values()].map((acc) => {
      const projectenLijst = [...acc.projecten.values()].sort((a, b) => String(b.project.nummer).localeCompare(String(a.project.nummer)));
      const lijstNamen = [...projectenLijst.map((x) => x.lijstNaam), ...(acc.algemeenLijst ? [acc.algemeenLijst] : [])];
      const aantal = lijstNamen.reduce((n, l) => n + (aantalIn.get(l) || 0), 0);
      return { ...acc, projecten: projectenLijst, lijstNamen, aantal };
    }).sort((a, b) => a.account.localeCompare(b.account, 'nl'));
    return { accounts, eigen: eigen.sort((a, b) => a.localeCompare(b, 'nl')), perNummer, perNaam, accountPerNaam };
  }, [boom, msLijsten, aantalIn, eigenLijsten, projecten]);
  const lijsten = useMemo(() => [{ naam: 'Algemeen', aantal: aantalIn.get('Algemeen') || 0 },
    ...indeling.eigen.map((naam) => ({ naam, aantal: aantalIn.get(naam) || 0 }))], [indeling, aantalIn]);
  const alleLijstNamen = useMemo(() => ['Algemeen',
    ...indeling.accounts.flatMap((acc) => acc.lijstNamen),
    ...indeling.eigen], [indeling]);
  const actiefAccount = typeof weergave === 'object' ? weergave.account : null;
  const accountVanLijst = (naam) => indeling.accounts.find((acc) => acc.lijstNamen.includes(naam))?.account || null;

  const vandaag = vandaagISO();
  const metDeadline = useMemo(() => (items || []).filter((t) => t.deadline).sort((a, b) => String(a.deadline).localeCompare(String(b.deadline))), [items]);
  // Mijn dag = vandaag én wat al over tijd is; achterstallig werk verstoppen is erger dan het tonen.
  const vandaagItems = useMemo(() => metDeadline.filter((t) => String(t.deadline).slice(0, 10) <= vandaag), [metDeadline, vandaag]);
  const zichtbaar = useMemo(() => {
    if (weergave === 'dag') return vandaagItems;
    if (weergave === 'gepland') return metDeadline;
    if (actieveLijst) return (items || []).filter((t) => (t.groep || 'Algemeen') === actieveLijst);
    if (actiefAccount) {
      const namen = new Set(indeling.accounts.find((a) => a.account === actiefAccount)?.lijstNamen || []);
      return (items || []).filter((t) => namen.has(t.groep || 'Algemeen'));
    }
    return items || [];
  }, [weergave, actieveLijst, actiefAccount, indeling, items, vandaagItems, metDeadline]);
  const openItem = (items || []).find((t) => t.id === openId) || null;

  const railKnop = (actief, label, aantal, opKlik, opVerwijder) => (
    <Box key={label} onClick={opKlik}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 1, pr: 0.5, py: 0.55, borderRadius: 1, cursor: 'pointer',
        bgcolor: actief ? '#eef2ff' : 'transparent', '&:hover': { bgcolor: actief ? '#eef2ff' : '#f8fafc' }, '&:hover .lijst-del': { opacity: 1 } }}>
      <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: actief ? 800 : 600, color: actief ? '#1e3a8a' : '#334155',
        flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={label}>{label}</Typography>
      {aantal > 0 && <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>{aantal}</Typography>}
      {opVerwijder && (
        <Tooltip title="Lijst verwijderen">
          <IconButton className="lijst-del" size="small" onClick={(e) => { e.stopPropagation(); opVerwijder(); }} sx={{ opacity: 0, transition: 'opacity 0.15s', p: 0.25 }}>
            <DeleteOutlineIcon sx={{ fontSize: 15, color: '#94a3b8' }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
  const railKop = (tekstKop) => (
    <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4, mt: 1.5, mb: 0.5, px: 1.25 }}>{tekstKop}</Typography>
  );

  // Ruimere rij, knoppen altijd zichtbaar (Lars): klik op de tekst opent het zijpaneel.
  const rij = (t, toonLijst = false) => {
    const laat = t.deadline && String(t.deadline).slice(0, 10) < vandaag;
    return (
      <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderBottom: '1px solid #f1f5f9',
        opacity: t.net_af ? 0.45 : 1, transition: 'opacity 0.4s', bgcolor: openId === t.id ? '#f8fafc' : 'transparent', '&:hover': { bgcolor: '#f8fafc' } }}>
        <Checkbox size="small" checked={!!t.net_af} onChange={() => vinkAf(t)} sx={{ p: 0.5 }} />
        <Box onClick={() => setOpenId(t.id)} sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 14.5, color: '#0f172a', textDecoration: t.net_af ? 'line-through' : 'none' }}>{t.title}</Typography>
          {(toonLijst && t.groep) || t.notes || t.checklist?.length ? (
            <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8', mt: 0.25 }} noWrap>
              {[toonLijst && t.groep ? t.groep : null,
                t.checklist?.length ? `☑ ${t.checklist.filter((c) => c.done).length} van ${t.checklist.length}` : null,
                t.notes ? `📝 ${String(t.notes).split('\n')[0]}` : null].filter(Boolean).join(' · ')}
            </Typography>
          ) : null}
        </Box>
        <Button size="small" variant="outlined" onClick={() => setOpenId(t.id)}
          sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, py: 0.25, px: 1, minWidth: 0, flexShrink: 0,
            color: laat ? '#b91c1c' : t.deadline ? '#334155' : '#94a3b8', borderColor: laat ? '#fca5a5' : '#e2e8f0' }}>
          ⚑ {t.deadline ? datumKort(t.deadline) : 'deadline'}
        </Button>
        <Tooltip title="Verwijderen">
          <IconButton size="small" onClick={() => verwijder(t)}><DeleteOutlineIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></IconButton>
        </Tooltip>
      </Box>
    );
  };
  const kader = (kinderen) => <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>{kinderen}</Box>;
  const groepKop = (label, aantal, rood = false) => (
    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 800, color: rood ? '#b91c1c' : '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5, mt: 1 }}>
      {label} {aantal !== undefined && <Typography component="span" sx={{ fontSize: 11.5, color: '#cbd5e1' }}>({aantal})</Typography>}
    </Typography>
  );
  const titelPerWeergave = weergave === 'dag' ? 'Mijn dag' : weergave === 'gepland' ? 'Gepland' : actiefAccount ? `${actiefAccount} · alle projecten` : actieveLijst || 'Alle to-do\'s';
  const boomFilter = boomZoek.trim().toLowerCase();
  const accountsZichtbaar = indeling.accounts.map((acc) => {
    if (!boomFilter) return acc;
    const accMatch = acc.account.toLowerCase().includes(boomFilter);
    const pr = accMatch ? acc.projecten : acc.projecten.filter((x) => `${x.project.nummer} ${x.project.naam}`.toLowerCase().includes(boomFilter));
    return (accMatch || pr.length) ? { ...acc, projecten: pr } : null;
  }).filter(Boolean);
  const boomRij = ({ sleutel, actief, diepte = 0, onClick, label, aantal, gedimd = false, extra }) => (
    <Box key={sleutel} onClick={onClick} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 1 + diepte * 1.5, pr: 0.5, py: 0.45, borderRadius: 1, cursor: 'pointer',
      bgcolor: actief ? '#eef2ff' : 'transparent', '&:hover': { bgcolor: actief ? '#eef2ff' : '#f8fafc' }, '&:hover .lijst-del': { opacity: 1 } }}>
      <Typography noWrap title={label} sx={{ fontFamily: FONT, fontSize: diepte ? 12.5 : 13, fontWeight: actief ? 800 : diepte ? 500 : 700,
        color: actief ? '#1e3a8a' : gedimd ? '#a0aec0' : diepte ? '#475569' : '#334155', flex: 1, minWidth: 0 }}>{label}</Typography>
      {aantal > 0 && <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: diepte ? '#6366f1' : '#94a3b8', bgcolor: diepte ? '#eef2ff' : 'transparent', px: diepte ? 0.7 : 0, borderRadius: 1 }}>{aantal}</Typography>}
      {extra}
    </Box>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader icon="playlist_add_check" title="Privé-to-do's" subtitle="Jouw Microsoft To Do — snel vangen, later plannen; alleen jij ziet dit"
      rightActions={false ? (
      <Button size="small" variant="outlined" startIcon={<CloudDownloadIcon sx={{ fontSize: 16 }} />} onClick={() => setImportOpen(true)} sx={{ textTransform: 'none', fontWeight: 700 }}>
      Importeer uit Microsoft To Do
      </Button>
      ) : undefined} />

      {/* Zelfde opbouw als Mijn Notes: links een boomkaart, rechts de inhoud (Joris, 6 sep: "voelt niet als één tool"). */}
      <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
      <Box sx={{ width: 270, flexShrink: 0, display: { xs: 'none', md: 'block' }, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff', p: 1, position: 'sticky', top: 16 }}>
        {railKnop(weergave === 'dag', 'Mijn dag', vandaagItems.length, () => setWeergave('dag'))}
        {railKnop(weergave === 'gepland', 'Gepland', metDeadline.length, () => setWeergave('gepland'))}
        {railKnop(weergave === 'alles', 'Alle to-do\'s', (items || []).length, () => setWeergave('alles'))}

        {railKop('Accounts')}
        <TextField size="small" fullWidth placeholder="Zoek account of project…" value={boomZoek} onChange={(e) => setBoomZoek(e.target.value)}
          sx={{ mb: 0.5, px: 0.5, '& .MuiInputBase-input': { py: 0.6, fontSize: 12.5 } }} />
        {accountsZichtbaar.length === 0 && (
          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8', px: 1.25, py: 0.5 }}>
            {boomFilter ? 'Geen account of project gevonden.' : 'Nog geen projectlijsten — kies een project via "＋ Project".'}
          </Typography>
        )}
        {accountsZichtbaar.map((acc) => {
          const open = boomFilter || openAccounts.has(acc.account);
          return (
            <Box key={acc.account}>
              {boomRij({ sleutel: `acc-${acc.account}`, actief: actiefAccount === acc.account, label: `${open ? '▾' : '▸'} ${acc.account}`, aantal: acc.aantal,
                onClick: () => { if (!open) toggleAccount(acc.account); setWeergave({ account: acc.account }); },
                extra: (
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleAccount(acc.account); }} sx={{ p: 0.25 }} aria-label={open ? 'Inklappen' : 'Uitklappen'}>
                    <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{open ? '–' : '+'}</Typography>
                  </IconButton>
                ) })}
              {open && (
                <>
                  {acc.algemeenLijst && boomRij({ sleutel: `alg-${acc.account}`, diepte: 1, actief: actieveLijst === acc.algemeenLijst,
                    label: '📂 Algemeen', aantal: aantalIn.get(acc.algemeenLijst) || 0, onClick: () => setWeergave({ lijst: acc.algemeenLijst }) })}
                  {acc.projecten.map((x) => boomRij({ sleutel: `p-${x.project.id}`, diepte: 1, actief: actieveLijst === x.lijstNaam,
                    label: `${x.project.nummer}. ${x.project.naam}`, aantal: x.aantal, gedimd: !x.aantal && !msLijsten.some((l) => l.displayName === x.lijstNaam),
                    onClick: () => setWeergave({ lijst: x.lijstNaam }),
                    extra: msLijsten.some((l) => l.displayName === x.lijstNaam) && !x.aantal ? (
                      <Tooltip title="Lege projectlijst verwijderen">
                        <IconButton className="lijst-del" size="small" onClick={(e) => { e.stopPropagation(); verwijderLijst(x.lijstNaam); }} sx={{ opacity: 0, transition: 'opacity 0.15s', p: 0.25 }}>
                          <DeleteOutlineIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                        </IconButton>
                      </Tooltip>
                    ) : null }))}
                </>
              )}
            </Box>
          );
        })}
        <Button size="small" onClick={() => setLijstOpen(true)} sx={{ mt: 0.5, justifyContent: 'flex-start', textTransform: 'none', fontFamily: NAV_FONT, fontWeight: 700, color: '#64748b', px: 1.25 }}>
          ＋ Project of lijst
        </Button>

        {railKop('Lijsten')}
        {lijsten.map((l) => railKnop(actieveLijst === l.naam, l.naam, l.aantal, () => setWeergave({ lijst: l.naam }), l.naam === 'Algemeen' ? null : () => verwijderLijst(l.naam)))}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ width: '100%' }}>

          <Typography sx={{ fontFamily: FONT, fontSize: 16, fontWeight: 800, color: '#0f172a', mb: 1.25 }}>
            {titelPerWeergave}
            {weergave === 'dag' && <Typography component="span" sx={{ fontFamily: FONT, fontSize: 12.5, color: '#94a3b8', ml: 1, fontWeight: 500 }}>deadline vandaag of eerder</Typography>}
          </Typography>

          {legacy.length > 0 && (
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: '#fffbeb', border: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#78350f', flex: 1, minWidth: 240 }}>
                Je privé-to-do's staan nu in <b>Microsoft To Do</b>. In het archief van deze app staan nog {legacy.length} oude to-do's
                {legacyImport.length > 0 && <> — {legacyImport.length} daarvan komen uit je eerdere To Do-import en staan dus al in To Do</>}
                {legacyEigen.length > 0 && <>{legacyImport.length > 0 ? ' en ' : ' — '}{legacyEigen.length} {legacyEigen.length === 1 ? 'is' : 'zijn'} hier aangemaakt</>}.
              </Typography>
              {legacyImport.length > 0 && (
                <Button size="small" variant="outlined" disabled={legacyBezig} onClick={ruimImportOp}
                  sx={{ textTransform: 'none', fontWeight: 700, color: '#92400e', borderColor: '#f59e0b' }}>
                  {legacyBezig ? 'Bezig…' : `Archiveer ${legacyImport.length} (staan al in To Do)`}
                </Button>
              )}
              {legacyEigen.length > 0 && (
                <Button size="small" variant="contained" disableElevation disabled={legacyBezig} onClick={zetLegacyOver}
                  sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' } }}>
                  {legacyBezig ? 'Bezig…' : `Zet ${legacyEigen.length} over naar To Do`}
                </Button>
              )}
            </Box>
          )}
          <TextField fullWidth autoFocus inputRef={invoerRef}
            placeholder={actieveLijst && actieveLijst !== 'Algemeen' ? `＋ To-do in "${actieveLijst}" (Enter)…` : actiefAccount ? `＋ To-do bij ${actiefAccount} zonder project (Enter)…` : weergave === 'dag' ? '＋ To-do voor vandaag (Enter)…' : '＋ To-do toevoegen (Enter)…'}
            value={tekst} onChange={(e) => setTekst(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); voegToe(); } }} disabled={bezig}
            sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />

          {items === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={22} /></Box>
          ) : zichtbaar.length === 0 ? (
            <Typography sx={{ fontFamily: FONT, color: '#94a3b8', textAlign: 'center', py: 6 }}>
              {weergave === 'dag' ? 'Niks voor vandaag — zet een ⚑ op een to-do of typ hierboven.' : weergave === 'gepland' ? 'Nog niks met een deadline.' : 'Niks te doen — typ hierboven en druk op Enter.'}
            </Typography>
          ) : weergave === 'alles' || actiefAccount ? (
            (() => {
              const accs = actiefAccount ? indeling.accounts.filter((a) => a.account === actiefAccount) : indeling.accounts;
              const blokken = [];
              for (const acc of accs) {
                const namen = [...(acc.algemeenLijst ? [acc.algemeenLijst] : []), ...acc.projecten.map((x) => x.lijstNaam)].filter((n) => aantalIn.get(n));
                if (!namen.length) continue;
                blokken.push(
                  <Box key={`acc-${acc.account}`} sx={{ mb: 2.5 }}>
                    {!actiefAccount && <Typography sx={{ fontFamily: NAV_FONT, fontSize: 13.5, fontWeight: 800, color: '#0f172a', mb: 0.5 }}>{acc.account}</Typography>}
                    {namen.map((naam) => (
                      <Box key={naam} sx={{ mb: 1.5 }}>
                        {groepKop(naam === acc.algemeenLijst ? 'Algemeen' : naam, aantalIn.get(naam))}
                        {kader((items || []).filter((t) => (t.groep || 'Algemeen') === naam).map((t) => rij(t)))}
                      </Box>
                    ))}
                  </Box>,
                );
              }
              if (!actiefAccount) {
                for (const l of lijsten.filter((x) => x.aantal)) {
                  blokken.push(
                    <Box key={`lijst-${l.naam}`} sx={{ mb: 2 }}>
                      {groepKop(l.naam, l.aantal)}
                      {kader((items || []).filter((t) => (t.groep || 'Algemeen') === l.naam).map((t) => rij(t)))}
                    </Box>,
                  );
                }
              }
              return blokken.length ? blokken : (
                <Typography sx={{ fontFamily: FONT, color: '#94a3b8', textAlign: 'center', py: 6 }}>Nog geen to-do's bij dit account — typ hierboven, die komt in "{actiefAccount}" (algemeen).</Typography>
              );
            })()
          ) : weergave === 'gepland' ? (
            (() => {
              const perDag = new Map();
              for (const t of zichtbaar) { const dag = String(t.deadline).slice(0, 10); if (!perDag.has(dag)) perDag.set(dag, []); perDag.get(dag).push(t); }
              return [...perDag.entries()].map(([dag, groep]) => (
                <Box key={dag} sx={{ mb: 2 }}>{groepKop(datumKort(dag), undefined, dag < vandaag)}{kader(groep.map((t) => rij(t, true)))}</Box>
              ));
            })()
          ) : kader(zichtbaar.map((t) => rij(t, weergave === 'dag')))}

          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#94a3b8', mt: 2.5 }}>
            Deze to-do's verschijnen ook in je <Link component={RouterLink} to="/weekplan">Weekplan</Link> om in te plannen.
            Projecttaken vind je onder <Link component={RouterLink} to="/mijn-taken">Mijn Taken</Link>.
            {' '}In Microsoft To Do zie je één lijst per project (“nummer. naam”); het account erboven komt uit onze projectenlijst.
          </Typography>
        </Box>
      </Box>
      </Box>

      <TodoPaneel item={openItem} lijsten={alleLijstNamen} onClose={() => setOpenId(null)} onWijzig={wijzig} onVerwijder={verwijder} onVinkAf={vinkAf} onSubtaak={subtaak} />

      {importOpen && <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} bestaande={items || []} onKlaar={() => { setImportOpen(false); laad(); }} />}
      <NieuweLijstDialog open={lijstOpen} onClose={() => setLijstOpen(false)} bestaande={alleLijstNamen}
        onKies={(naam) => { maakLijst(naam); setWeergave({ lijst: naam }); setLijstOpen(false); invoerRef.current?.focus(); }} />
    </Box>
  );
}
